import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Loader2, TrendingDown, Bell, Check } from 'lucide-react';

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthOf(iso) { return (iso || todayISO()).slice(0, 7); }
function addMonths(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
// Compact money label so it fits inside a calendar cell (₹9.6k, ₹1.2L).
function compact(n, symbol) {
  if (n == null) return '';
  if (n >= 100000) return `${symbol}${(n / 100000).toFixed(n >= 1000000 ? 0 : 1)}L`;
  if (n >= 1000) return `${symbol}${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `${symbol}${Math.round(n)}`;
}
const SYM = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$' };

// Date picker with a per-day fare heatmap. When both origin & destination are known it fetches
// /flights/month-prices and shows the cheapest fare under each day + lets travellers flip months
// (up to +11, the fare-data horizon). Past dates are always disabled. Falls back to a plain
// calendar when no route/prices are available.
export const FlightPriceCalendar = ({ label, value, onChange, minDate, origin, destination,
                                      roundTrip = false, tripDays = 7, disabled = false, testid }) => {
  const { currency } = useApp();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(monthOf(value));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const today = todayISO();
  const floor = minDate && minDate > today ? minDate : today;
  const sym = SYM[currency] || '';

  useEffect(() => { if (open) setView(monthOf(value)); /* eslint-disable-next-line */ }, [open]);

  useEffect(() => {
    if (!open || !origin || !destination) { setData(null); return; }
    let active = true;
    setLoading(true);
    api.monthPrices({ origin, destination, month: view, round_trip: roundTrip, trip_days: tripDays, currency })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, view, origin, destination, roundTrip, tripDays, currency]);

  const priceByDate = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { if (d.price != null) m[d.date] = d; });
    return m;
  }, [data]);

  const [y, mo] = view.split('-').map(Number);
  const firstWeekday = (new Date(y, mo - 1, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(y, mo, 0).getDate();
  const curYm = monthOf(today);
  const maxYm = addMonths(curYm, 11);

  const cheapestDate = data?.cheapest_date;
  const displayVal = value
    ? new Date(value + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Select date';

  const pick = (iso) => { onChange(iso); setOpen(false); };

  return (
    <div>
      {label && <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</label>}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled}
            className={`w-full flex items-center gap-2 rounded-xl border border-input px-3 h-12 text-sm font-medium ${disabled ? 'bg-secondary/60 opacity-60 cursor-not-allowed' : 'bg-card'}`}
            data-testid={testid}>
            <CalIcon className="h-4 w-4 text-accent shrink-0" />
            <span className="truncate">{displayVal}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] p-3" data-testid={`${testid}-popover`}>
          <div className="flex items-center justify-between mb-2">
            <button type="button" disabled={view <= curYm} onClick={() => setView(addMonths(view, -1))}
              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed" data-testid="cal-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold">{MONTHS[mo - 1]} {y}</span>
            <button type="button" disabled={view >= maxYm} onClick={() => setView(addMonths(view, 1))}
              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed" data-testid="cal-next-month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {origin && destination && (
            <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1 min-h-[16px]">
              {loading ? <><Loader2 className="h-3 w-3 animate-spin" /> Loading {roundTrip ? 'round-trip' : 'one-way'} fares…</>
                : data?.has_data ? <><TrendingDown className="h-3 w-3 text-emerald-600" /> Cheapest {roundTrip ? 'round-trip' : 'one-way'} fares · lowest in green</>
                  : 'Live fares unavailable for this route/month'}
            </p>
          )}
          <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
            {WD.map((d) => <span key={d} className="text-[10px] font-medium text-muted-foreground">{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const iso = `${view}-${String(day).padStart(2, '0')}`;
              const isPast = iso < floor;
              const isSel = iso === value;
              const info = priceByDate[iso];
              const isCheapest = info && cheapestDate === iso;
              return (
                <button key={iso} type="button" disabled={isPast} onClick={() => pick(iso)}
                  className={`flex flex-col items-center justify-center rounded-lg py-1 h-11 transition-colors
                    ${isPast ? 'opacity-25 cursor-not-allowed' : 'hover:bg-secondary'}
                    ${isSel ? 'bg-accent text-accent-foreground hover:bg-accent' : ''}
                    ${!isSel && isCheapest ? 'bg-emerald-50 border border-emerald-300' : ''}`}
                  data-testid={`cal-day-${iso}`}>
                  <span className={`text-xs leading-none ${isSel ? 'font-bold' : 'font-medium'}`}>{day}</span>
                  {info && <span className={`text-[9px] leading-tight mt-0.5 ${isSel ? 'text-accent-foreground/90' : isCheapest ? 'text-emerald-700 font-semibold' : info.estimated ? 'text-muted-foreground/60 italic' : 'text-muted-foreground'}`}>{info.estimated ? '~' : ''}{compact(info.price, sym)}</span>}
                </button>
              );
            })}
          </div>
          {roundTrip && data?.has_estimates && (
            <p className="text-[10px] text-muted-foreground/80 mt-1.5 italic" data-testid="cal-estimate-note">
              ~ indicative one-way fare shown where a round-trip price isn’t cached for that day.
            </p>
          )}
          {origin && destination && (
            <FareAlertBar origin={origin} destination={destination} departDate={value}
              roundTrip={roundTrip} tripDays={tripDays} currency={currency} sym={sym} />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Inline "notify me when fares drop" subscribe form shown at the bottom of the calendar.
const FareAlertBar = ({ origin, destination, departDate, roundTrip, tripDays, currency, sym }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!email || !/.+@.+\..+/.test(email)) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      await api.createFareAlert({ origin, destination, email, depart_date: departDate,
        round_trip: roundTrip, trip_days: tripDays, currency,
        target_price: target ? Number(target) : null });
      setDone(true);
      toast.success('Price alert set — we’ll email you when fares drop.');
    } catch {
      toast.error('Could not set the alert. Try again.');
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-xs text-emerald-700" data-testid="fare-alert-done">
      <Check className="h-4 w-4" /> Alert set for {origin}→{destination}. We’ll email you on a drop.
    </div>
  );

  return (
    <div className="mt-3 pt-3 border-t border-border">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="fare-alert-open"
          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-accent hover:underline">
          <Bell className="h-3.5 w-3.5" /> Alert me when {origin}→{destination} fares drop
        </button>
      ) : (
        <div className="space-y-2" data-testid="fare-alert-form">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            className="w-full h-9 rounded-lg border border-input px-2 text-sm" data-testid="fare-alert-email" />
          <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={`Notify under (${sym}) — optional`}
            className="w-full h-9 rounded-lg border border-input px-2 text-sm" data-testid="fare-alert-target" />
          <button type="button" onClick={submit} disabled={busy} data-testid="fare-alert-submit"
            className="w-full h-9 rounded-lg bg-accent text-accent-foreground text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Set price alert
          </button>
        </div>
      )}
    </div>
  );
};
